import { useEffect, useState } from 'react';
import { detectWakeAny, normalizePhrase } from '@elena/shared';
import { api, type SttEngine, type SttInfo } from './api';
import { mic, micSupported } from './mic';
import { session as appSession } from './session';
import {
  listen,
  markMicBusy,
  MAX_LISTEN_MS,
  NOTHING_SAID_MS,
  recognitionLang,
  SILENCE_MS,
  isSpeaking,
  stripAck,
  stripAckEnd,
  type ListenHandlers,
} from './speech';
import {
  loadAzureSdk,
  openSession,
  refillKey,
  SttFailure,
  type CloudEngine,
  type SttSession,
} from './sttEngines';
import { findUtterance, SpeechTracker } from './vad';

/**
 * The mic, the best way this server allows: a cloud recognizer (Soniox, ElevenLabs Scribe or
 * Azure, chosen on the server) fed by Elena's own mic tap, and the browser's recognizer when
 * there is none, the mic can't be opened, or this device is set to Google.
 *
 * After "Hej Elena, pusti David Guetta…" the wake word recognizer (Chrome) only heard the name
 * right; the whole sentence is still in the mic tap's memory, so it goes to the real recognizer
 * from the start of the sentence, and listening carries on in case you are not done yet.
 */

export interface BestListenHandlers extends ListenHandlers {
  /** The seed came from "Hej Elena, …": the same words are in the mic tap's memory. */
  fromWake?: boolean;
}

/** After a wake sentence that already ended: how long to wait in case more is coming. */
const CONTINUE_MS = 1200;

// ---------------------------------------------------------------------------------------------
// which engine, shared with the widget's status line

let info: { at: number; value: SttInfo } | null = null;
let infoLoad: Promise<SttInfo | null> | null = null;

async function sttInfo(): Promise<SttInfo | null> {
  // the Azure token inside lives 10 minutes; reuse it for 7
  if (info && Date.now() - info.at < 7 * 60_000) return info.value;
  infoLoad ??= api
    .stt()
    .then((value) => {
      info = { at: Date.now(), value };
      emit();
      return value;
    })
    .catch(() => null)
    .finally(() => {
      infoLoad = null;
    });
  return infoLoad;
}

const azureOf = (i: SttInfo): { token: string; region: string } | null =>
  i.azure ?? (i.token && i.region ? { token: i.token, region: i.region } : null);

/** The engine this device uses now ('browser' = Chrome / Edge's own). */
function engineOf(i: SttInfo | null): SttEngine {
  if (appSession.sttEngine() === 'google' || !micSupported() || !i?.enabled) return 'browser';
  const engine = i.engine ?? 'azure';
  if (engine === 'azure' && !azureOf(i)) return 'browser';
  return engine;
}

/**
 * The server's recognizer for Elena's phone (#/caller), whatever this device prefers: the player's
 * voice arrives over WebRTC, which only a cloud recognizer can hear. null = there is none.
 */
export async function serverRecognizer(): Promise<{
  engine: CloudEngine;
  phrases: string[];
  azure: { token: string; region: string } | null;
} | null> {
  // a fresh Azure token each time: a call can come hours after the page loaded
  info = null;
  const i = await sttInfo();
  if (!i?.enabled) return null;
  const engine = i.engine ?? 'azure';
  const azure = azureOf(i);
  if (engine === 'browser' || (engine === 'azure' && !azure)) return null;
  if (engine === 'soniox' || engine === 'elevenlabs') void refillKey(engine);
  return { engine, phrases: i.phrases ?? [], azure };
}

export const engineLabels: Record<SttEngine, string> = {
  soniox: 'Soniox',
  elevenlabs: 'ElevenLabs Scribe',
  azure: 'Azure',
  browser: 'Chrome',
};

export interface VoiceStatus {
  engine: SttEngine;
  /** Set when the last message fell back to another recognizer, and why. */
  fallback: string | null;
}
let status: VoiceStatus = { engine: 'browser', fallback: null };
const watchers = new Set<(s: VoiceStatus) => void>();
function emit(patch: Partial<VoiceStatus> = {}): void {
  status = { ...status, engine: engineOf(info?.value ?? null), ...patch };
  watchers.forEach((w) => w(status));
}
export const voiceStatus = (): VoiceStatus => status;

/** The engine in use and the last fallback, kept current for a component. */
export function useVoiceStatus(): VoiceStatus {
  const [value, setValue] = useState(status);
  useEffect(() => {
    warmVoice();
    setValue(status);
    return onVoiceStatus(setValue);
  }, []);
  return value;
}
export function onVoiceStatus(w: (s: VoiceStatus) => void): () => void {
  watchers.add(w);
  return () => void watchers.delete(w);
}

/** Loads the engine, a spare key and the Azure SDK ahead of time, so the mic never waits. */
export function warmVoice(): void {
  void sttInfo().then((i) => {
    const engine = engineOf(i);
    if (engine === 'soniox' || engine === 'elevenlabs') void refillKey(engine);
    if (engine === 'azure' || (i && azureOf(i))) loadAzureSdk().catch(() => undefined);
  });
}

/**
 * While "Hej Elena" is being listened for, keeps the mic tap running, so the sentence that
 * wakes Elena is already in memory for the real recognizer. Returns the release function.
 */
export function holdMicForWake(): () => void {
  let held = false;
  let released = false;
  void sttInfo().then((i) => {
    if (released || engineOf(i) === 'browser') return;
    held = true;
    void mic.acquire().then((ok) => {
      if (!ok) emit({ fallback: 'Mikrofon nije dostupan Eleni, sluša Chrome.' });
    });
  });
  return () => {
    released = true;
    if (held) mic.release();
  };
}

// ---------------------------------------------------------------------------------------------
// listening

/** "Hej Elena, pusti Cecu" → "pusti Cecu"; when the name was misheard, a leading greeting goes. */
function afterWake(text: string): string {
  const wake = detectWakeAny([text]);
  if (wake.heard) return wake.command;
  const words = text.split(/\s+/).filter(Boolean);
  let i = 0;
  while (i < words.length && /^(hej|hey|ej|aj|e)$/.test(normalizePhrase(words[i] ?? ''))) i++;
  return words.slice(i).join(' ');
}

const ERROR_TEXT = 'Nisam te razumela, ponovi molim te.';

/**
 * Starts listening; returns a stop function (stopping early still sends what was heard).
 * Same handlers as the browser's listen(), so the callers did not change.
 */
export function listenBest(h: BestListenHandlers): () => void {
  let stopped = false;
  let stopFn: (() => void) | null = null;
  const browser = () => {
    if (stopped) h.onEnd();
    else stopFn = listen(h);
  };

  void (async () => {
    const i = await sttInfo();
    const engine = engineOf(i);
    if (engine === 'browser' || !i) return browser();
    if (!(await mic.acquire())) {
      emit({ fallback: 'Mikrofon nije dostupan Eleni, sluša Chrome.' });
      return browser();
    }
    if (stopped) {
      mic.release();
      h.onEnd();
      return;
    }
    stopFn = runCloud(engine, i, h, browser);
  })();

  return () => {
    stopped = true;
    stopFn?.();
  };
}

function runCloud(
  engine: CloudEngine,
  i: SttInfo,
  h: BestListenHandlers,
  browser: () => void,
): () => void {
  const seed = h.seed?.trim() ?? '';
  const fromWake = Boolean(h.fromWake && seed);
  const lang = recognitionLang();
  const phrases = i.phrases ?? [];
  const azure = azureOf(i);
  const started = performance.now();
  const recorded: Int16Array[] = [];
  const tracker = new SpeechTracker();
  let finished = false;

  /** No "Hej Elena", and none of Elena's own "Mhm?" before or after your words. */
  const clean = (text: string) => stripAckEnd(stripAck(fromWake ? afterWake(text) : text));
  const display = (text: string) => clean(text) || seed;
  const open = (which: CloudEngine): SttSession =>
    openSession(which, {
      lang,
      phrases,
      azure,
      onPartial: (text) => !finished && h.onInterim(display(text)),
    });

  let current = open(engine);
  const push = (pcm: Int16Array) => {
    recorded.push(pcm);
    current.push(pcm);
  };

  markMicBusy(true);
  if (seed) h.onInterim(seed);

  if (fromWake) {
    // the sentence that woke Elena, from where it started
    const pcm = mic.recent();
    const span = findUtterance(pcm);
    if (span) {
      push(pcm.slice(span.start));
      tracker.markSpeech(performance.now());
    }
  }

  const unsubscribe = mic.onAudio((frame) => {
    if (finished) return;
    push(frame);
    const now = performance.now();
    // Elena's own "Mhm?" is not you talking: it neither starts nor stretches the wait
    if (!isSpeaking()) tracker.push(frame, now);
    const limit = tracker.heard ? (fromWake ? CONTINUE_MS : SILENCE_MS) : NOTHING_SAID_MS;
    if (tracker.quietFor(now) > limit || now - started > MAX_LISTEN_MS) stop();
  });

  const report = (s: SttSession) => {
    if (s.seconds > 0)
      void api.sttUsage({ seconds: s.seconds, engine: s.engine }).catch(() => undefined);
  };

  const deliver = (guesses: string[]) => {
    const texts = guesses.map(clean).filter(Boolean);
    const text = texts[0] ?? (fromWake ? seed : '');
    if (text) h.onFinal(text, texts.slice(1));
    else if (!tracker.heard) h.onError('Nisam čula ništa. Probaj ponovo.');
  };

  const end = () => {
    markMicBusy(false);
    mic.release();
    h.onEnd();
  };

  /** The paid engine failed: Azure hears the same recorded audio, or Chrome's words are used. */
  const fallback = async (err: SttFailure) => {
    if (err.message === 'canceled') return end();
    report(current);
    if (current.engine !== 'azure' && azure && recorded.length) {
      emit({ fallback: `${engineLabels[current.engine]} nije uspeo, slušala je Azure.` });
      const second = open('azure');
      current = second;
      for (const pcm of recorded) second.push(pcm);
      try {
        deliver(await second.finish());
        report(second);
        return end();
      } catch {
        report(second);
      }
    }
    if (fromWake) {
      // Chrome's words from the wake word: worse, but better than asking again
      emit({ fallback: `${engineLabels[engine]} nije uspeo, poslala sam ono što je čuo Chrome.` });
      h.onFinal(seed, []);
      return end();
    }
    if (!tracker.heard) {
      // nothing said yet: the browser recognizer takes over from here
      emit({ fallback: `${engineLabels[engine]} ne radi, sluša Chrome.` });
      markMicBusy(false);
      mic.release();
      return browser();
    }
    emit({ fallback: `${engineLabels[engine]} nije uspeo.` });
    h.onError(
      err.quota
        ? 'Kredit za prepoznavanje je potrošen. Prebaci na Google u Podešavanjima.'
        : ERROR_TEXT,
    );
    end();
  };

  function stop(): void {
    if (finished) return;
    finished = true;
    unsubscribe();
    const s = current;
    s.finish()
      .then((guesses) => {
        report(s);
        if (status.fallback) emit({ fallback: null });
        deliver(guesses);
        end();
      })
      .catch((err: unknown) =>
        fallback(err instanceof SttFailure ? err : new SttFailure(String(err))),
      );
  }

  return stop;
}
